import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { Menu, ConfigProvider } from 'antd';
import {
  AppstoreOutlined,
  AimOutlined,
  EyeOutlined,
  UnorderedListOutlined,
  ScheduleOutlined,
  BarChartOutlined,
  CloudSyncOutlined,
  CloudUploadOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  SendOutlined,
  SettingOutlined,
  ControlOutlined,
  ToolOutlined,
  UserOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
  ClusterOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SyncOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import AppHeader from '@/components/AppHeader';
import BuildBadge from '@/components/BuildBadge';
import {
  readSidebarCollapsePreference,
  sidebarCollapsedForViewport,
  type SidebarCollapsePreference,
} from './collapsePreference';
import { useCurrentUser } from '@/common/auth/adminAcl';
import {
  resolveWorkspace,
  isMenuKeyInWorkspace,
  type WorkspaceKey,
} from '@/common/workspace';

import './index.less';

// 左栏信息架构 v2 —— 见 docs/platform-refactor-v1.md §1
// 顶层分组:
//   - overview                 概览（landing）
//   - planning-*               PNC 仿真（任务列表 / Daily）—— sim_type='planning'
//   - perception-*             感知仿真（任务列表 / Daily / 评估报表）—— sim_type='perception'
//   - scene-*                  仿真场景（管理 / 生成 / 质检）
//   - schedule-*               计划任务（定时列表 / 打版触发）
//   - system-*                 系统（后台管理 / 我的账号）
//   - hil-* / bvt-*            HIL 稳定性工作区（顶栏横向分页切过去才可见）

const OVERVIEW_KEY = 'overview';
const SIM_DASHBOARD_KEY = 'sim-dashboard';

// PNC 仿真（planning + control 合称，用 sim_type='planning' 作 URL key）
const PLANNING_GROUP_KEY = 'planning';
const PLANNING_TASKS_KEY = 'planning-tasks';
const PLANNING_DAILY_KEY = 'planning-daily';

// HIL platform
const HIL_GROUP_KEY = 'hil';
const HIL_NATIVE_TASKS_KEY = 'hil-native-tasks';
const HIL_REPORTS_KEY = 'hil-reports';

// BVT 回灌（台架稳定性回灌，独立顶层分组）
const BVT_GROUP_KEY = 'bvt';
const BVT_TASKS_KEY = 'bvt-tasks';
const BVT_CASES_KEY = 'bvt-cases';
const BVT_CHECKERS_KEY = 'bvt-checkers';

// 台架管理：与 HIL 仿真 / BVT 回灌**并列**的顶层单项（不是 HIL 分组下的子项）。
// 这一页同时覆盖 HIL 与 BVT 两种能力的台架，挂在任一分组下都会误导归属。
// 第一步只读（平台目录 ↔ 执行端实时状态对照），不含任何写操作。
const BENCH_DIRECTORY_KEY = 'bench-directory';

// 感知仿真
const PERCEPTION_GROUP_KEY = 'perception';
const PERCEPTION_TASKS_KEY = 'perception-tasks';
const PERCEPTION_DAILY_KEY = 'perception-daily';
// 评估报表：多 case 指标汇总，按 jobId 取数（不走 daily 的 source 闸门）
const PERCEPTION_REPORT_KEY = 'perception-report';

// 仿真场景
const SCENE_GROUP_KEY = 'scene';
const SCENE_MANAGEMENT_KEY = 'scene-management';
const SCENE_GENERATION_KEY = 'scene-generation';
const SCENE_QC_KEY = 'scene-qc';
const PREDICTION_UPDATE_KEY = 'prediction-update';
const TAG_MANAGEMENT_KEY = 'tag-management';

// 模型管理
const MODEL_GROUP_KEY = 'model-management';
const MODEL_RELEASES_KEY = 'model-releases';
const MODEL_QUERY_KEY = 'model-query';
const MODEL_UPLOAD_KEY = 'model-upload';
const MODEL_SYNC_KEY = 'model-sync';

// 计划任务
const SCHEDULE_GROUP_KEY = 'schedule';
const SCHEDULE_LIST_KEY = 'schedule-list';
const RELEASE_TRIGGER_KEY = 'release-triggers';

// 系统
const SYSTEM_GROUP_KEY = 'system';
const ADMIN_KEY = 'admin';
const SCENE_COLLECTION_KEY = 'scene-collection';
const SYS_CONFIG_KEY = 'sys-config';
const ACCOUNT_KEY = 'account';
const ABOUT_KEY = 'about';

// 折叠状态持久化 key
const COLLAPSE_STORAGE_KEY = 'sim:sidebar:collapsed';
const NARROW_VIEWPORT_QUERY = '(max-width: 767px)';

// 「概览」页 2026-09 起接入了「项目 · 资源占用」两张卡 (simulation / simulation-gpu),
// 已作为登录后默认 landing (router `/` → `/overview`),Sidebar 顶部同步露出。
// 关掉时:改回 false,同时把 router `/` 的 Navigate 目标改回原路径。
const SHOW_OVERVIEW = true;

const readCollapsedPreference = (): SidebarCollapsePreference => {
  try {
    return readSidebarCollapsePreference(localStorage.getItem(COLLAPSE_STORAGE_KEY));
  } catch {
    return null;
  }
};

const isNarrowViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof window.matchMedia === 'function'
    ? window.matchMedia(NARROW_VIEWPORT_QUERY).matches
    : window.innerWidth <= 767;
};

const writeCollapsedPreference = (collapsed: boolean): void => {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 当前工作区由路由反推（见 common/workspace.ts）：左栏只渲染本工作区的分组，
  // 切顶栏分页 = 换整条左栏；URL 变化（含分享链接 / 刷新 / 直接粘地址）都会重新
  // 反推，不会出现「URL 在 /bvt 但左栏还是仿真平台菜单」的不一致。
  const workspace: WorkspaceKey = resolveWorkspace(location.pathname);

  // ── 主内容区滚动位置记忆 ──────────────────────────────────
  // 按 pathname 存/恢复 .sidebar-content-inner 的 scrollTop（sessionStorage），
  // 使从子页面（如场景编辑）返回时停在离开前的滚动位置，而不是回到顶部。
  const contentRef = useRef<HTMLDivElement>(null);
  const SCROLL_KEY_PREFIX = 'sim:scroll:';

  // 滚动时实时把当前 pathname 的 scrollTop 写进 sessionStorage（节流靠浏览器
  // 的 passive scroll，够用）。这样离开页面前位置已存好，不依赖路由切换时机。
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => {
      sessionStorage.setItem(SCROLL_KEY_PREFIX + location.pathname, String(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  // 进入新路径时恢复其记忆位置：内容异步加载（列表数据回来才有高度），故轮询
  // 等 scrollHeight 够高再设 scrollTop，最多重试 ~2s；无记忆则滚到顶。
  useLayoutEffect(() => {
    const saved = Number(sessionStorage.getItem(SCROLL_KEY_PREFIX + location.pathname));
    const target = Number.isFinite(saved) && saved > 0 ? saved : 0;
    if (target === 0) {
      if (contentRef.current) contentRef.current.scrollTop = 0;
      return;
    }
    let tries = 0;
    let raf = 0;
    const tryRestore = () => {
      const el = contentRef.current;
      if (!el) return;
      // 内容已够高能滚到目标（留 4px 容差）→ 落位；否则继续等下一帧。
      if (el.scrollHeight - el.clientHeight >= target - 4) {
        el.scrollTop = target;
        return;
      }
      if (tries++ < 120) raf = requestAnimationFrame(tryRestore); // ~2s @60fps
    };
    raf = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  // admin 兜底权限：admin 角色 or menu:admin → 全部通过
  const { hasPerm } = useCurrentUser();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const collapsePreference = useRef<SidebarCollapsePreference>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const preference = readCollapsedPreference();
    collapsePreference.current = preference;
    return sidebarCollapsedForViewport(preference, isNarrowViewport());
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(NARROW_VIEWPORT_QUERY);
    const syncAutomaticCollapse = (event: MediaQueryListEvent | MediaQueryList) => {
      if (collapsePreference.current !== null) return;
      setCollapsed(event.matches);
      if (event.matches) setOpenKeys([]);
    };
    query.addEventListener?.('change', syncAutomaticCollapse);
    if (!query.addEventListener) query.addListener(syncAutomaticCollapse);
    return () => {
      query.removeEventListener?.('change', syncAutomaticCollapse);
      if (!query.removeEventListener) query.removeListener(syncAutomaticCollapse);
    };
  }, []);

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    collapsePreference.current = next;
    writeCollapsedPreference(next);
    setCollapsed(next);
    if (next) setOpenKeys([]);
  }, [collapsed]);

  // 根据路由 + sim_type query 更新高亮 & 展开状态
  useEffect(() => {
    const path = location.pathname;
    const simType = new URLSearchParams(location.search).get('sim_type') || 'planning';

    const activate = (selKey: string, openKey: string) => {
      setSelectedKeys([selKey]);
      if (!collapsed) setOpenKeys([openKey]);
    };

    if (path === '/' || path === '/overview') {
      setSelectedKeys([OVERVIEW_KEY]);
      // overview 顶层单项，不展开分组
      return;
    }
    if (path.startsWith('/sim-dashboard')) {
      // 执行人排行榜归入「系统」分组，高亮并展开该组。
      activate(SIM_DASHBOARD_KEY, SYSTEM_GROUP_KEY);
      return;
    }
    if (path.startsWith('/hil/native/tasks')) {
      activate(HIL_NATIVE_TASKS_KEY, HIL_GROUP_KEY);
      return;
    }
    if (path.startsWith('/hil/reports')) {
      activate(HIL_REPORTS_KEY, HIL_GROUP_KEY);
      return;
    }
    if (path.startsWith('/hil/resources/directory')) {
      // 台架管理是顶层单项，不展开任何分组。
      setSelectedKeys([BENCH_DIRECTORY_KEY]);
      return;
    }
    if (path.startsWith('/bvt/cases')) {
      activate(BVT_CASES_KEY, BVT_GROUP_KEY);
      return;
    }
    if (path.startsWith('/bvt/checkers')) {
      activate(BVT_CHECKERS_KEY, BVT_GROUP_KEY);
      return;
    }
    if (path === '/bvt' || path.startsWith('/bvt/')) {
      activate(BVT_TASKS_KEY, BVT_GROUP_KEY);
      return;
    }
    if (path.startsWith('/job-tasks/')) {
      // 任务列表下钻页（Task 列表）：高亮对应仿真类型的任务列表 + 展开该组。
      const grp = simType === 'perception' ? PERCEPTION_GROUP_KEY : PLANNING_GROUP_KEY;
      const sel = simType === 'perception' ? PERCEPTION_TASKS_KEY : PLANNING_TASKS_KEY;
      activate(sel, grp);
      return;
    }
    if (path === '/task-list' || path.startsWith('/task-list/')) {
      const grp = simType === 'perception' ? PERCEPTION_GROUP_KEY : PLANNING_GROUP_KEY;
      const sel = simType === 'perception' ? PERCEPTION_TASKS_KEY : PLANNING_TASKS_KEY;
      activate(sel, grp);
      return;
    }
    if (path.startsWith('/perception-report')) {
      // 报表页只属于感知，不看 sim_type query
      activate(PERCEPTION_REPORT_KEY, PERCEPTION_GROUP_KEY);
      return;
    }
    if (path === '/daily-simulation' || path.startsWith('/daily')) {
      const grp = simType === 'perception' ? PERCEPTION_GROUP_KEY : PLANNING_GROUP_KEY;
      const sel = simType === 'perception' ? PERCEPTION_DAILY_KEY : PLANNING_DAILY_KEY;
      activate(sel, grp);
      return;
    }
    if (path === '/scene-management') {
      activate(SCENE_MANAGEMENT_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/tag-management') || path.startsWith('/add-scenes-to-tag')) {
      activate(TAG_MANAGEMENT_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/scene-generation')) {
      activate(SCENE_GENERATION_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/scene-qc')) {
      activate(SCENE_QC_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/prediction-update')) {
      activate(PREDICTION_UPDATE_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/scene-annotate')) {
      // 场景标注页统一从场景管理入口点进,侧栏高亮场景管理。
      activate(SCENE_MANAGEMENT_KEY, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/scene-bos-files/')) {
      // 场景编辑页菜单高亮跟随来源，与「返回」目的地一致：
      // generation→场景生成 / compare(任务对比页)→对应仿真任务列表 / 其余→场景管理。
      const from = new URLSearchParams(location.search).get('from');
      if (from === 'compare') {
        const grp = simType === 'perception' ? PERCEPTION_GROUP_KEY : PLANNING_GROUP_KEY;
        const sel = simType === 'perception' ? PERCEPTION_TASKS_KEY : PLANNING_TASKS_KEY;
        activate(sel, grp);
        return;
      }
      const sel = from === 'generation' ? SCENE_GENERATION_KEY : SCENE_MANAGEMENT_KEY;
      activate(sel, SCENE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/model-management')) {
      if (path.startsWith('/model-management/models')) {
        activate(MODEL_QUERY_KEY, MODEL_GROUP_KEY);
      } else if (path.startsWith('/model-management/upload')) {
        activate(MODEL_UPLOAD_KEY, MODEL_GROUP_KEY);
      } else if (path.startsWith('/model-management/sync')) {
        activate(MODEL_SYNC_KEY, MODEL_GROUP_KEY);
      } else {
        // /model-management 或 /model-management/releases 都落到 releases
        activate(MODEL_RELEASES_KEY, MODEL_GROUP_KEY);
      }
      return;
    }
    if (path === '/schedule' || path === '/schedule/list') {
      activate(SCHEDULE_LIST_KEY, SCHEDULE_GROUP_KEY);
      return;
    }
    if (path === '/schedule/release-triggers') {
      activate(RELEASE_TRIGGER_KEY, SCHEDULE_GROUP_KEY);
      return;
    }
    if (path.startsWith('/admin')) {
      activate(ADMIN_KEY, SYSTEM_GROUP_KEY);
      return;
    }
    if (path.startsWith('/scene-collection')) {
      activate(SCENE_COLLECTION_KEY, SYSTEM_GROUP_KEY);
      return;
    }
    if (path.startsWith('/sys-config')) {
      activate(SYS_CONFIG_KEY, SYSTEM_GROUP_KEY);
      return;
    }
    if (path.startsWith('/account-settings')) {
      activate(ACCOUNT_KEY, SYSTEM_GROUP_KEY);
      return;
    }
    if (path.startsWith('/about')) {
      activate(ABOUT_KEY, SYSTEM_GROUP_KEY);
      return;
    }
  }, [location.pathname, location.search, collapsed]);

  const openChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const onSelect = ({ key }: { key: string }) => {
    setSelectedKeys([key]);
    switch (key) {
      case OVERVIEW_KEY:
        navigate('/overview');
        return;
      case SIM_DASHBOARD_KEY:
        navigate('/sim-dashboard');
        return;

      // PNC 仿真
      case PLANNING_TASKS_KEY:
        navigate('/task-list?sim_type=planning');
        return;
      case PLANNING_DAILY_KEY:
        navigate('/daily-simulation?sim_type=planning');
        return;

      // HIL
      case HIL_NATIVE_TASKS_KEY:
        navigate('/hil/native/tasks');
        return;
      case HIL_REPORTS_KEY:
        navigate('/hil/reports');
        return;

      // 台架管理（与 HIL / BVT 并列的顶层单项）
      case BENCH_DIRECTORY_KEY:
        navigate('/hil/resources/directory');
        return;

      // BVT 回灌
      case BVT_TASKS_KEY:
        navigate('/bvt/tasks');
        return;
      case BVT_CASES_KEY:
        navigate('/bvt/cases');
        return;
      case BVT_CHECKERS_KEY:
        navigate('/bvt/checkers');
        return;

      // 感知仿真
      case PERCEPTION_TASKS_KEY:
        navigate('/task-list?sim_type=perception');
        return;
      case PERCEPTION_DAILY_KEY:
        navigate('/daily-simulation?sim_type=perception');
        return;
      case PERCEPTION_REPORT_KEY:
        navigate('/perception-report');
        return;

      // 仿真场景
      case SCENE_MANAGEMENT_KEY:
        navigate('/scene-management');
        return;
      case TAG_MANAGEMENT_KEY:
        navigate('/tag-management');
        return;
      case SCENE_GENERATION_KEY:
        navigate('/scene-generation');
        return;
      case SCENE_QC_KEY:
        navigate('/scene-qc');
        return;
      case PREDICTION_UPDATE_KEY:
        navigate('/prediction-update');
        return;

      // 模型管理
      case MODEL_RELEASES_KEY:
        navigate('/model-management/releases');
        return;
      case MODEL_QUERY_KEY:
        navigate('/model-management/models');
        return;
      case MODEL_UPLOAD_KEY:
        navigate('/model-management/upload');
        return;
      case MODEL_SYNC_KEY:
        navigate('/model-management/sync');
        return;

      // 计划任务
      case SCHEDULE_LIST_KEY:
        navigate('/schedule/list');
        return;
      case RELEASE_TRIGGER_KEY:
        navigate('/schedule/release-triggers');
        return;

      // 系统
      case ADMIN_KEY:
        navigate('/admin');
        return;
      case SCENE_COLLECTION_KEY:
        navigate('/scene-collection');
        return;
      case SYS_CONFIG_KEY:
        navigate('/sys-config');
        return;
      case ACCOUNT_KEY:
        navigate('/account-settings');
        return;
      case ABOUT_KEY:
        navigate('/about');
        return;
    }
  };

  // 菜单结构（v2）—— 概览暂未接入指标数据，按 SHOW_OVERVIEW 开关控制显隐
  const allMenuItems = [
    ...(SHOW_OVERVIEW
      ? [{
          key: OVERVIEW_KEY,
          label: '概览',
          icon: <AppstoreOutlined />,
        }]
      : []),
    {
      key: PLANNING_GROUP_KEY,
      label: 'PNC仿真',
      icon: <AimOutlined />,
      children: [
        { key: PLANNING_TASKS_KEY, label: '任务列表', icon: <UnorderedListOutlined /> },
        { key: PLANNING_DAILY_KEY, label: 'Daily', icon: <ScheduleOutlined /> },
      ],
    },
    {
      key: PERCEPTION_GROUP_KEY,
      label: '感知仿真',
      icon: <EyeOutlined />,
      children: [
        { key: PERCEPTION_TASKS_KEY, label: '任务列表', icon: <UnorderedListOutlined /> },
        { key: PERCEPTION_DAILY_KEY, label: 'Daily', icon: <ScheduleOutlined /> },
        { key: PERCEPTION_REPORT_KEY, label: '评估报表', icon: <BarChartOutlined /> },
      ],
    },
    {
      key: HIL_GROUP_KEY,
      label: 'HIL 仿真',
      icon: <ExperimentOutlined />,
      children: [
        { key: HIL_NATIVE_TASKS_KEY, label: '任务列表', icon: <UnorderedListOutlined /> },
        { key: HIL_REPORTS_KEY, label: '评估报表', icon: <BarChartOutlined /> },
      ],
    },
    {
      key: BVT_GROUP_KEY,
      label: 'BVT 回灌',
      // 不能与 HIL 仿真共用 ExperimentOutlined：两个顶层分组图标相同会分不清。
      // 用「循环回流」的同步箭头表达"回灌"（把实车数据灌回台架再跑检查项）。
      icon: <SyncOutlined />,
      children: [
        { key: BVT_TASKS_KEY, label: '任务列表', icon: <UnorderedListOutlined /> },
        { key: BVT_CASES_KEY, label: '回灌用例', icon: <DatabaseOutlined /> },
        { key: BVT_CHECKERS_KEY, label: '检查项', icon: <SafetyCertificateOutlined /> },
      ],
    },
    {
      // 与 HIL 仿真 / BVT 回灌并列的顶层单项：单页、无子项，所以直接给 key + label
      // （不需要 children）。图标用「机架」语义，与 HIL 的 ExperimentOutlined、
      // BVT 的 SyncOutlined 都不同，三个并列项一眼可辨。
      key: BENCH_DIRECTORY_KEY,
      label: '台架管理',
      icon: <ClusterOutlined />,
    },
    {
      key: SCENE_GROUP_KEY,
      label: '仿真场景',
      icon: <EnvironmentOutlined />,
      children: [
        { key: SCENE_MANAGEMENT_KEY, label: '场景管理', icon: <FolderOpenOutlined /> },
        { key: TAG_MANAGEMENT_KEY, label: '标签管理', icon: <TagsOutlined /> },
        { key: SCENE_GENERATION_KEY, label: '场景生成', icon: <ThunderboltOutlined /> },
        // 场景质检：无 menu:scene-qc 权限则不进 Sidebar；路由层有 RequirePermission 兜底防 URL 直填。
        ...(hasPerm('menu:scene-qc')
          ? [{ key: SCENE_QC_KEY, label: '场景质检', icon: <SafetyCertificateOutlined /> }]
          : []),
        // 数据更新（PR_DATA）：无 menu:prediction:update 权限则不进 Sidebar；路由层同码兜底。
        ...(hasPerm('menu:prediction:update')
          ? [{ key: PREDICTION_UPDATE_KEY, label: '数据更新', icon: <DatabaseOutlined /> }]
          : []),
      ],
    },
    {
      key: MODEL_GROUP_KEY,
      label: '模型管理',
      icon: <DatabaseOutlined />,
      children: [
        { key: MODEL_RELEASES_KEY, label: '版本查询', icon: <AppstoreOutlined /> },
        { key: MODEL_QUERY_KEY, label: '模型查询', icon: <UnorderedListOutlined /> },
        { key: MODEL_UPLOAD_KEY, label: '模型上传', icon: <CloudUploadOutlined /> },
        { key: MODEL_SYNC_KEY, label: '模型同步', icon: <CloudSyncOutlined /> },
      ],
    },
    {
      key: SCHEDULE_GROUP_KEY,
      label: '计划任务',
      icon: <ClockCircleOutlined />,
      children: [
        { key: SCHEDULE_LIST_KEY, label: '定时列表', icon: <FieldTimeOutlined /> },
        { key: RELEASE_TRIGGER_KEY, label: '打版触发', icon: <SendOutlined /> },
      ],
    },
    {
      key: SYSTEM_GROUP_KEY,
      label: '系统',
      icon: <SettingOutlined />,
      children: [
        // 后台管理：无 menu:admin 权限则不出现在 Sidebar；路由层同名 RequirePermission 兜底。
        ...(hasPerm('menu:admin')
          ? [{ key: ADMIN_KEY, label: '后台管理', icon: <ControlOutlined /> }]
          : []),
        // 执行人排行榜（原「仿真看板」页,KPI/趋势已迁入概览页公开露出,此处只保留
        // 需授权的排行榜）：无 menu:sim-dashboard 权限则不进 Sidebar；路由层有
        // RequirePermission 兜底防 URL 直填,后端 /ranking 端点 require_permission 兜底。
        ...(hasPerm('menu:sim-dashboard')
          ? [{ key: SIM_DASHBOARD_KEY, label: '执行人排行榜', icon: <BarChartOutlined /> }]
          : []),
        // 场景集合运维 / 系统参数运维：原后台管理页的「场景集合」「参数配置」两个
        // Tab,已拆成独立权限点提为一级菜单（与后台管理平级）。无对应权限则不出现；
        // 路由层 RequirePermission 兜底。
        ...(hasPerm('menu:scene-collection')
          ? [{ key: SCENE_COLLECTION_KEY, label: '场景集合运维', icon: <FolderOpenOutlined /> }]
          : []),
        ...(hasPerm('menu:sys-config')
          ? [{ key: SYS_CONFIG_KEY, label: '系统参数运维', icon: <ToolOutlined /> }]
          : []),
        { key: ACCOUNT_KEY, label: '我的账号', icon: <UserOutlined /> },
        { key: ABOUT_KEY, label: '关于', icon: <InfoCircleOutlined /> },
      ],
    },
  ];

  // 只留当前工作区的分组：切顶栏分页时整条左栏换内容。
  // 归属关系集中在 common/workspace.ts，分组定义本身仍只写一份，
  // 避免图标唯一性 / 导航行为在多处重复维护。
  const menuItems = allMenuItems.filter((item) => isMenuKeyInWorkspace(item.key, workspace));

  return (
    <div className="sidebar-layout">
      {/* 顶栏横跨整宽：品牌「仿真平台」+ 全局工作区（SIL / HIL）+ 用户区。
          顶栏与左栏同为深色，组成一个 L 形全局框架，内容区嵌在其中。 */}
      <AppHeader />
      <div className="sidebar-body">
        <div className={`sidebar-shell ${collapsed ? 'sidebar-shell--collapsed' : ''}`}>
          <div className="sidebar-menu-wrapper">
            <ConfigProvider
              theme={{
                token: {
                  colorBgContainer: 'transparent',
                  colorText: '#c8c9ce',
                },
                components: {
                  Menu: {
                    itemBg: 'transparent',
                    itemColor: '#9b9da3',
                    itemHoverBg: 'rgba(255,255,255,0.06)',
                    itemHoverColor: '#e0e0e4',
                    itemActiveBg: 'rgba(255,255,255,0.06)',
                    itemSelectedBg: 'rgba(94,106,210,0.18)',
                    itemSelectedColor: '#c4b5fd',
                    subMenuItemBg: 'transparent',
                    itemBorderRadius: 6,
                    subMenuItemBorderRadius: 6,
                    itemMarginInline: 4,
                    itemMarginBlock: 2,
                    collapsedWidth: 64,
                    collapsedIconSize: 18,
                    popupBg: '#1a1a2e',
                  },
                },
              }}
            >
              <Menu
                mode="inline"
                theme="dark"
                className="sidebar-menu"
                inlineCollapsed={collapsed}
                inlineIndent={16}
                openKeys={collapsed ? [] : openKeys}
                onOpenChange={openChange}
                onSelect={onSelect}
                items={menuItems}
                selectedKeys={selectedKeys}
              />
            </ConfigProvider>
          </div>
          <div className="sidebar-footer">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={toggleCollapsed}
              aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
              title={collapsed ? '展开侧边栏' : '收起侧边栏'}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              {!collapsed && <span className="sidebar-toggle-text">收起</span>}
            </button>
            <BuildBadge collapsed={collapsed} />
          </div>
        </div>
        <div className="sidebar-content">
          <div className="sidebar-content-inner" ref={contentRef}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
